import type { ChangeEvent } from 'react';
import { Box, FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface ModuleVisibilityFlags {
  inspectionsEnabled: boolean;
  serviceBookEnabled: boolean;
  partsOrderingEnabled: boolean;
}

interface ModuleVisibilitySettingsProps {
  value: ModuleVisibilityFlags;
  onChange: (next: ModuleVisibilityFlags) => void;
  disabled?: boolean;
}

/**
 * Workshop module switches (#2646). Missing stored flags stay on; this form
 * writes only the three keys so a save cannot reset WhatsApp via PATCH.
 */
export function ModuleVisibilitySettings({
  value,
  onChange,
  disabled = false,
}: ModuleVisibilitySettingsProps) {
  const { t } = useTranslation('settings');

  const toggle = (key: keyof ModuleVisibilityFlags) => (event: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, [key]: event.target.checked });
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('modules.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('modules.description')}
      </Typography>
      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={value.inspectionsEnabled}
                onChange={toggle('inspectionsEnabled')}
                disabled={disabled}
                slotProps={{ input: { 'aria-label': t('modules.inspections') } }}
              />
            }
            label={
              <Box>
                <Typography variant="body1">{t('modules.inspections')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('modules.inspectionsHint')}
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={value.serviceBookEnabled}
                onChange={toggle('serviceBookEnabled')}
                disabled={disabled}
                slotProps={{ input: { 'aria-label': t('modules.serviceBook') } }}
              />
            }
            label={
              <Box>
                <Typography variant="body1">{t('modules.serviceBook')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('modules.serviceBookHint')}
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={value.partsOrderingEnabled}
                onChange={toggle('partsOrderingEnabled')}
                disabled={disabled}
                slotProps={{ input: { 'aria-label': t('modules.partsOrdering') } }}
              />
            }
            label={
              <Box>
                <Typography variant="body1">{t('modules.partsOrdering')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('modules.partsOrderingHint')}
                </Typography>
              </Box>
            }
          />
        </Stack>
      </Paper>
    </Box>
  );
}
